<template>
  <div class="container">
    <div v-if="pageLoading" class="inline-loader-container">
      <div class="loader-content text-center">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 fw-bold text-muted">Fetching item information...</p>
      </div>
    </div>

    <div v-else class="main-card animate-fade-in">
      
      <div class="inner-box mb-4">
        <div class="section-header-row">
          <h4 class="section-title">1. Edit Item Identity</h4>
          
          <div class="d-flex align-items-center gap-4">
            <div class="d-flex align-items-center gap-2">
              <span :class="itemDetails.isReturned ? 'text-primary' : 'text-muted'" class="fw-bold status-label">
                {{ itemDetails.isReturned ? 'RETURNABLE' : 'NON-RETURNABLE' }}
              </span>
              <label class="switch">
                <input type="checkbox" v-model="itemDetails.isReturned">
                <span class="slider round"></span>
              </label>
            </div>

            <div class="d-flex align-items-center gap-2">
              <span :class="itemDetails.isActive ? 'text-success' : 'text-danger'" class="fw-bold status-label">
                {{ itemDetails.isActive ? 'ACTIVE' : 'INACTIVE' }}
              </span>
              <label class="switch">
                <input type="checkbox" v-model="itemDetails.isActive">
                <span class="slider round"></span>
              </label>
            </div>
          </div>
        </div>

        <div class="profile-layout mt-3">
          <div class="image-profile-section">
            <div class="image-preview-container">
              <img 
                :src="itemDetails.itemImageBase64 ? (itemDetails.itemImageBase64.startsWith('data') ? itemDetails.itemImageBase64 : 'data:image/png;base64,' + itemDetails.itemImageBase64) : defaultPlaceholder" 
                alt="Item Preview" 
                class="profile-img"
              />
              <div class="image-overlay">
                <div class="action-button-row">
                  <label for="itemImage" class="overlay-btn" title="Change Image">
                    <i class="fa fa-upload"></i>
                  </label>
                  <button v-if="itemDetails.itemImageBase64" @click="removeImage" class="overlay-btn remove-btn" title="Remove Image">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
            <input type="file" id="itemImage" @change="handleImageUpload" accept="image/*" class="d-none">
            <p class="text-muted small mt-2">Item Image</p>
          </div>

          <div class="details-form-section">
            <div class="form-grid">
              <div class="form-group full-width">
                <label>Item Name<span class="text-danger">*</span></label>
                <input type="text" v-model="itemDetails.itemName" class="form-control" placeholder="Enter name">
              </div>
              <div class="form-group">
                <label>SAP Code<span class="text-danger">*</span></label>
                <input type="text" v-model="itemDetails.sapCode" class="form-control" placeholder="Enter SAP code">
              </div>
              <div class="form-group">
                <label>Item Type<span class="text-danger">*</span></label>
                <select v-model="itemDetails.itemType" class="form-control">
                  <option disabled value="">Select Item Type</option>
                  <option v-for="type in ItemTypeList" :key="type.id" :value="type.id">{{ type.name }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="inner-box">
        <div class="section-header-row">
          <h4 class="section-title mb-0">2. Manage Variants</h4>
          <button class="btn btn-primary btn-sm m-0" @click="openAddVariantModal" :disabled="!itemDetails.isActive">
            Add Variant
          </button>
        </div>
        
        <div class="table-responsive mt-3">
          <table v-if="variants.length > 0" class="table">
            <thead>
              <tr>
                <th style="width: 50px;">#</th>
                <th>Variant Name</th>
                <th class="text-center">Status</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(variant, index) in variants" :key="index" :class="{'primary-variant-row': variant.isParent}">
                <td>{{ index + 1 }}</td>
                <td>
                  <div class="d-flex flex-column">
                    <span class="variant-text" :class="{'text-muted': variant.isParent}">{{ variant.name }}</span>
                    <small v-if="variant.isParent" class="text-secondary">(Primary Item Variant)</small>
                  </div>
                </td>
                <td class="text-center">
                  <label class="switch sm" :class="{'disabled-control': variant.isParent || !itemDetails.isActive}">
                    <input 
                      type="checkbox" 
                      v-model="variant.isActive" 
                      :disabled="variant.isParent || !itemDetails.isActive"
                    >
                    <span class="slider round"></span>
                  </label>
                </td>
                <td class="text-center">
                    <i class="fa fa-edit me-3 action-icon" 
                       :class="(variant.isParent || !itemDetails.isActive) ? 'disabled-link' : 'text-primary'" 
                       @click="(!variant.isParent && itemDetails.isActive) && openEditVariantModal(index)"></i>
                    
                    <i v-if="!variant.isParent && !variant.id" 
                       class="fa fa-trash text-danger action-icon" 
                       @click="itemDetails.isActive && confirmDelete(index)"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="form-actions">
        <button class="btn btn-danger" @click="goBack" :disabled="loading">Cancel</button>
        <button class="btn btn-success ms-2" @click="updateItem" :disabled="loading">
          <span v-if="loading"><i class="fa fa-spinner fa-spin"></i> Updating...</span>
          <span v-else>Update Item</span>
        </button>
      </div>
    </div>

    <div v-if="showAddVariantModal || showEditVariantModal" class="custom-modal-overlay">
      <div class="custom-modal">
        <h5>{{ showEditVariantModal ? 'Edit Variant' : 'Add Variant' }}</h5>
        <input type="text" class="form-control mt-3" v-model="currentVariantName" placeholder="Variant name" @keyup.enter="saveVariant">
        <div class="d-flex justify-content-end mt-4 gap-2">
          <button class="btn btn-secondary btn-sm" @click="closeVariantModal">Cancel</button>
          <button class="btn btn-primary btn-sm" @click="saveVariant">{{ showEditVariantModal ? 'Update' : 'Add' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchItemType, getItemDetails, editItem } from '@/services/dataService'; 
import { useToast } from "vue-toastification";

export default {
  name: "UpdateItemForm",
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      token: localStorage.getItem('token_set') || '',
      itemId: this.$route.params.id, 
      loading: false,
      pageLoading: true,
      ItemTypeList: [],
      defaultPlaceholder: 'https://cdn-icons-png.flaticon.com/512/1160/1160358.png',
      itemDetails: { 
        itemName: '', 
        itemType: '', 
        sapCode: '', 
        itemImageBase64: null, 
        isActive: true,
        isReturned: false
      },
      variants: [],
      showAddVariantModal: false,
      showEditVariantModal: false,
      currentVariantName: '',
      editingVariantIndex: null
    };
  },
  watch: {
    'itemDetails.isActive'(newValue) {
      this.variants = this.variants.map(variant => ({
        ...variant,
        isActive: newValue
      }));
    }
  },
  async created() {
    try {
      await Promise.all([this.loadItemType(), this.loadInitialData()]);
    } finally {
      this.pageLoading = false;
    }
  },
  methods: {
    async loadItemType() {
      const response = await fetchItemType(this.token);
      this.ItemTypeList = response.data.data || [];
    },
    async loadInitialData() {
      const response = await getItemDetails(this.token, this.itemId);
      if (response.data.status === 'success') {
        const item = response.data.data.item_details;
        const variants = response.data.data.variants;
        this.itemDetails = {
          itemName: item.item_name,
          sapCode: item.sap_code,
          itemType: item.item_type_id,
          isActive: item.is_active === "1",
          isReturned: item.is_returned === "1",
          itemImageBase64: response.data.data.image_data
        };
        this.variants = variants.map(v => ({
          id: v.id,
          name: v.variant_name,
          isActive: v.is_active === "1",
          isParent: v.is_parent_item === "1"
        }));
      }
    },
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (file && file.size <= 1024 * 1024) {
        const reader = new FileReader();
        reader.onload = (e) => { this.itemDetails.itemImageBase64 = e.target.result.split(',')[1]; };
        reader.readAsDataURL(file);
      } else if (file) {
        this.toast.error("Image must be less than 1MB");
      }
    },
    removeImage() { this.itemDetails.itemImageBase64 = null; },
    openAddVariantModal() { this.currentVariantName = ''; this.showAddVariantModal = true; },
    openEditVariantModal(index) {
      this.editingVariantIndex = index;
      this.currentVariantName = this.variants[index].name;
      this.showEditVariantModal = true;
    },
    closeVariantModal() { this.showAddVariantModal = false; this.showEditVariantModal = false; },
    saveVariant() {
      if (!this.currentVariantName.trim()) return;
      if (this.showEditVariantModal) {
        this.variants[this.editingVariantIndex].name = this.currentVariantName;
      } else {
        this.variants.push({ name: this.currentVariantName, isActive: true, isParent: false, id: null });
      }
      this.closeVariantModal();
    },
    confirmDelete(index) {
      this.variants.splice(index, 1);
    },
    goBack() { this.$router.push('/items'); },
    async updateItem() {
      this.loading = true;
      const payload = {
        status: "success",
        data: {
          item_details: {
            id: this.itemId,
            item_name: this.itemDetails.itemName,
            sap_code: this.itemDetails.sapCode,
            item_type_id: this.itemDetails.itemType,
            is_active: this.itemDetails.isActive ? "1" : "0",
            is_returned: this.itemDetails.isReturned ? "1" : "0",
          },
          existing_variants: this.variants.filter(v => v.id && !v.isParent).map(v => ({
            id: v.id,
            variant_name: v.name,
            is_active: v.isActive ? "1" : "0"
          })),
          new_variants: this.variants.filter(v => !v.id).map(v => v.name),
          image_data: this.itemDetails.itemImageBase64
        }
      };
      try {
        await editItem(this.token, payload);
        this.toast.success("Item updated successfully!");
        this.goBack();
      } catch (error) { this.toast.error("Update failed."); }
      finally { this.loading = false; }
    }
  }
};
</script>

<style scoped>
.inline-loader-container {
  display: flex; justify-content: center; align-items: center; min-height: 400px; width: 100%;
}
.animate-fade-in { animation: fadeIn 0.4s ease-in-out; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

.container { padding: 10px; max-width: 1100px; margin: auto; }
.main-card { background: #fff; border-radius: 8px; padding: 25px;  }
.inner-box { border: 1px solid #f0f0f0; background: #fafafa; padding: 20px; border-radius: 6px; }
.section-title { font-size: 16px; font-weight: 700; color: #444; text-transform: uppercase; }

.profile-layout { display: flex; gap: 30px; }
.image-profile-section { display: flex; flex-direction: column; align-items: center; width: 150px; }
.image-preview-container { position: relative; width: 130px; height: 130px; border-radius: 10px; overflow: hidden; border: 2px solid #ddd; background: #fff; }
.profile-img { width: 100%; height: 100%; object-fit: contain; }
.image-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.3); display: flex; justify-content: center; align-items: center; opacity: 0; transition: 0.3s; }
.image-preview-container:hover .image-overlay { opacity: 1; }
.action-button-row { display: flex; gap: 10px; }
.overlay-btn { background: #fff; width: 32px; height: 32px; border-radius: 50%; display: flex; justify-content: center; align-items: center; cursor: pointer; border: none; color: #333; }
.remove-btn:hover { background: #dc3545; color: #fff; }

.details-form-section { flex: 1; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
.full-width { grid-column: 1 / -1; }
.form-group label { display: block; font-weight: 600; margin-bottom: 5px; font-size: 13px; color: #555; }
.form-control { width: 100%; padding: 8px 12px; border: 1px solid #ccc; border-radius: 4px; }

/* Switch Styling */
.switch { position: relative; display: inline-block; width: 44px; height: 22px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #ccc; transition: .4s; }
.slider:before { position: absolute; content: ""; height: 16px; width: 16px; left: 3px; bottom: 3px; background-color: white; transition: .4s; }
input:checked + .slider { background-color: #28a745; }
input:checked + .slider:before { transform: translateX(22px); }
.slider.round { border-radius: 34px; }
.slider.round:before { border-radius: 50%; }
.switch.sm { width: 36px; height: 18px; }
.switch.sm input:checked + .slider:before { transform: translateX(18px); }
.switch.sm .slider:before { height: 12px; width: 12px; }

.primary-variant-row { background-color: #f2f2f2 !important; }
.disabled-control { cursor: not-allowed; opacity: 0.6; }
.disabled-link { cursor: not-allowed; opacity: 0.5; pointer-events: none; }

.section-header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; }
.form-actions { display: flex; justify-content: flex-end; margin-top: 25px; }
.table { width: 100%; border-collapse: collapse; background: #fff; font-size: 14px; }
.table th, .table td { padding: 12px; border: 1px solid #eee; text-align: left; }
.table th { background: #f8f9fa; }
.action-icon { cursor: pointer; }
.custom-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.custom-modal { background: #fff; padding: 25px; border-radius: 8px; width: 400px; }
</style>